import { useEffect, useState } from 'react';
import { Alert, Box, Button, Icon, Spinner, Textarea } from '@amzn/awsui-components-react';
import {
  getIvtClient,
  UpdateElementCommand,
  type AssetSummary,
  type AssetDetail,
} from '../../../client/IvtHubClient';
import { isElementFilled, ELEMENT_NAMES } from '../../../utils/element';
import MarkdownRenderer from '../../../../components/ChatPanel/MarkdownRenderer';
import AssetKbFiles from './AssetKbFiles';
import AssetUcArsenal from './AssetUcArsenal';
// Reuse the KB / arsenal styles from the New Assets detail panel; the 8-element
// cards use this page's own styles (ivt-elem-card2*, in AssetDetailPage.css).
import '../../NewAssetsPage/components/NewAssetsDetail.css';
import '../../NewAssetsPage/NewAssetsPage.css';
import { denialHint } from '../../../utils/authorization';

/**
 * AssetInfoPanel — the "Information" body of the re-design Asset Detail page (M2):
 * the `8-Element | Knowledge Base` switch + 八要素 grid (read / whole-panel edit /
 * expanded single-card) + Related (关联 UC 库存弹药).
 *
 * Ported from NewAssetsDetail's body (8-element/KB/Related) MINUS its name/stages
 * header — the M2 page (AssetDetailPage) supplies its own breadcrumb + Details card
 * header, so this panel only owns the Information区 + Related. This is an
 * INTENTIONAL copy (not a shared extraction): NewAssetsDetail is slated for M4
 * deprecation, so we don't refactor it to share — that would risk the live old
 * page for no lasting benefit. Leaf components ElementCard / NewAssetKb are reused
 * as-is; the Related block uses this page's own Figma-aligned AssetUcArsenal (the
 * old NewAssetUcArsenal stays on the old page). The GetAsset fetch is owned by the
 * page (useAssetDetail) and passed in as
 * `detail` so the header and this panel share ONE detail and reload together.
 */
export default function AssetInfoPanel({
  asset,
  detail,
  loading,
  error,
  reload,
  arsenalRefreshKey = 0,
}: {
  asset: AssetSummary;
  /** Shared GetAsset detail, owned by the page (useAssetDetail) — the header and
   *  this panel read the SAME detail and reload together. */
  detail: AssetDetail | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Bumped by the page after a header save so the UC arsenal refetches GetUcFull —
   *  it renders the selected asset's name, which a rename changes. */
  arsenalRefreshKey?: number;
}) {
  const assetId = asset.assetId ?? null;

  // Information view: 八要素 grid (default) ↔ KB file browser. LOCAL state.
  const [detailView, setDetailView] = useState<'elements' | 'kb'>('elements');
  // Per-card inline edit (Figma: each card has its own ✎; clicking it turns THAT
  // card's content into a textarea — no whole-panel edit, no expanded view).
  const [editingElementNumber, setEditingElementNumber] = useState<number | null>(null);
  const [draftContent, setDraftContent] = useState('');
  const [savingElement, setSavingElement] = useState(false);
  const [elementSaveError, setElementSaveError] = useState<string | null>(null);

  // Reset the LOCAL edit state when the selected asset changes (the detail fetch
  // itself is owned by the page's useAssetDetail hook).
  useEffect(() => {
    setEditingElementNumber(null);
  }, [assetId]);

  // ALWAYS render all 8 element slots (1-8), even when the asset has none / only
  // some — a missing element renders as an empty placeholder card (Figma). Look up
  // each slot by elementNumber; absent → a synthetic EMPTY element.
  const byNumber = new Map((detail?.elements ?? []).map((e) => [e.elementNumber, e]));
  const elements = Array.from({ length: 8 }, (_, i) => {
    const num = i + 1;
    return byNumber.get(num) ?? { elementNumber: num, status: 'EMPTY' as const, content: undefined };
  });
  // Two independent columns (Figma): odd slots (1,3,5,7) left, even (2,4,6,8)
  // right — each column stacks and grows with its cards' content independently.
  const leftCol = elements.filter((e) => (e.elementNumber ?? 0) % 2 === 1);
  const rightCol = elements.filter((e) => (e.elementNumber ?? 0) % 2 === 0);

  const enterElementEdit = (num: number, content: string) => {
    setEditingElementNumber(num);
    setDraftContent(content);
    setElementSaveError(null);
  };
  const cancelElementEdit = () => {
    setEditingElementNumber(null);
    setElementSaveError(null);
  };
  const handleElementSave = async (num: number) => {
    if (!assetId) return;
    setSavingElement(true);
    setElementSaveError(null);
    try {
      await getIvtClient().send(
        new UpdateElementCommand({ assetId, elementNumber: num, content: draftContent }),
      );
      setEditingElementNumber(null);
      reload();
    } catch (e) {
      setElementSaveError(denialHint(e) ?? ((e as Error).message || '保存失败'));
    } finally {
      setSavingElement(false);
    }
  };

  // One card renderer — read (content w/ preserved line breaks) or inline-edit.
  const renderCard = (e: (typeof elements)[number]) => {
    const num = e.elementNumber ?? 0;
    const filled = isElementFilled(e.status, e.content);
    const editingThis = editingElementNumber === num;
    return (
      <div key={num} className="ivt-elem-card2">
        <div className="ivt-elem-card2__head">
          <span className="ivt-elem-card2__title">{ELEMENT_NAMES[num] ?? `要素 ${num}`}</span>
          {editingThis ? (
            <span className="ivt-elem-card2__actions">
              <Button
                variant="inline-icon"
                iconName="check"
                ariaLabel={`保存 ${ELEMENT_NAMES[num] ?? `要素 ${num}`}`}
                loading={savingElement}
                onClick={() => handleElementSave(num)}
              />
              <Button
                variant="inline-icon"
                iconName="close"
                ariaLabel={`取消编辑 ${ELEMENT_NAMES[num] ?? `要素 ${num}`}`}
                disabled={savingElement}
                onClick={cancelElementEdit}
              />
            </span>
          ) : (
            <Button
              variant="inline-icon"
              iconName="edit"
              ariaLabel={`编辑 ${ELEMENT_NAMES[num] ?? `要素 ${num}`}`}
              onClick={() => enterElementEdit(num, e.content ?? '')}
            />
          )}
        </div>
        {editingThis ? (
          <div className="ivt-elem-card2__input">
            <Textarea
              value={draftContent}
              onChange={({ detail }) => setDraftContent(detail.value)}
              placeholder="输入八要素内容…"
              disabled={savingElement}
              rows={6}
              ariaLabel={`${ELEMENT_NAMES[num] ?? `要素 ${num}`} 内容`}
            />
            {elementSaveError && (
              <div className="ivt-na-detail__save-error">
                <Box variant="small" color="text-status-error">
                  <Icon name="status-warning" size="small" variant="error" /> {elementSaveError}
                </Box>
              </div>
            )}
          </div>
        ) : filled ? (
          // Render the element content as Markdown (headings/bold/lists/tables/code)
          // — reuses the chat panel's dependency-free MarkdownRenderer, which also
          // preserves line breaks via its block parsing.
          <div className="ivt-elem-card2__content">
            <MarkdownRenderer markdown={e.content ?? ''} />
          </div>
        ) : (
          <div className="ivt-elem-card2__empty">未填写</div>
        )}
      </div>
    );
  };

  return (
    <div className="ivt-asset-info">
      {/* Information header: title + 8-Element | Knowledge Base switch. No global
          edit button — each element card carries its own ✎ (Figma). */}
      <div className="ivt-asset-info__bar">
        <h2 className="ivt-asset-info__title">Information</h2>
        <div className="ivt-asset-info__actions">
          <div className="ivt-asset-info__switch" role="tablist" aria-label="Information 视图切换">
            <button
              type="button"
              role="tab"
              aria-selected={detailView === 'elements'}
              className={`ivt-asset-info__seg${detailView === 'elements' ? ' ivt-asset-info__seg--active' : ''}`}
              onClick={() => setDetailView('elements')}
            >
              8-Element
            </button>
            {/* Standalone divider between the two segments — NOT tied to either
                segment's selected state, so it never gets covered by the active
                pill nor disappears when a side is selected. */}
            <span className="ivt-asset-info__divider" aria-hidden="true" />
            <button
              type="button"
              role="tab"
              aria-selected={detailView === 'kb'}
              className={`ivt-asset-info__seg${detailView === 'kb' ? ' ivt-asset-info__seg--active' : ''}`}
              onClick={() => setDetailView('kb')}
            >
              Knowledge Base
            </button>
          </div>
        </div>
      </div>

      {/* KB flip-side — real KB file management (upload + list + status), A-line. */}
      {detailView === 'kb' && <AssetKbFiles assetId={asset.assetId ?? ''} />}

      {/* 八要素 element view. */}
      {detailView === 'elements' && loading && (
        <div className="ivt-detail__loading">
          <Spinner size="large" />
          <span>加载八要素…</span>
        </div>
      )}
      {detailView === 'elements' && !loading && error && (
        <Alert
          type="error"
          header="无法加载 Asset 详情"
          action={<Button onClick={() => reload()}>重试</Button>}
        >
          {error}
        </Alert>
      )}
      {detailView === 'elements' && !loading && !error && !detail && (
        <div className="ivt-detail__empty">未找到该 Asset（{assetId}）。</div>
      )}

      {detailView === 'elements' && !loading && !error && detail && (
        <>
          {/* Two independent columns; cards grow with content, columns don't align
              row-to-row (Figma). Odd elements left, even right. */}
          <div className="ivt-elem-cards2">
            <div className="ivt-elem-cards2__col">{leftCol.map(renderCard)}</div>
            <div className="ivt-elem-cards2__col">{rightCol.map(renderCard)}</div>
          </div>

          {/* Related — 关联 UC 库存弹药. */}
          <div className="ivt-asset-info__related">
            <h2 className="ivt-asset-info__related-title">Related</h2>
            {/* arsenalRefreshKey: a rename changes the name this card renders for the
                selected asset, but not the industry/solutionMapId its fetch keys off. */}
            <AssetUcArsenal links={detail.solutionMapLinks ?? []} refreshKey={arsenalRefreshKey} />
          </div>
        </>
      )}
    </div>
  );
}
